import { reportLovableError } from "./lovable-error-reporting";

export type ClientErrorRecord = {
  at: string;
  route: string;
  source: "window.onerror" | "unhandledrejection" | "error-boundary";
  message: string;
  stack?: string;
  componentStack?: string;
  boundary?: string;
};

declare global {
  interface Window {
    __shieldsErrors?: ClientErrorRecord[];
  }
}

const MAX_RECORDS = 25;
const RESIZE_OBSERVER_NOISE = [
  "ResizeObserver loop completed with undelivered notifications",
  "ResizeObserver loop limit exceeded",
];

export function describeUnknownError(error: unknown): { message: string; stack?: string } {
  if (error instanceof Error) {
    return { message: error.message, ...(error.stack ? { stack: error.stack } : {}) };
  }
  if (error instanceof Response) {
    return { message: `Response ${error.status}${error.url ? ` at ${error.url}` : ""}` };
  }
  if (typeof error === "string") return { message: error };
  try {
    return { message: JSON.stringify(error) ?? String(error) };
  } catch {
    return { message: String(error) };
  }
}

function isBenignResizeObserverError(error: unknown) {
  const { message } = describeUnknownError(error);
  return RESIZE_OBSERVER_NOISE.some((text) => message.includes(text));
}

export function recordClientError(
  error: unknown,
  context: { source: ClientErrorRecord["source"]; componentStack?: string; boundary?: string },
) {
  if (typeof window === "undefined" || isBenignResizeObserverError(error)) return;
  const { message, stack } = describeUnknownError(error);
  const record: ClientErrorRecord = {
    at: new Date().toISOString(),
    route: window.location.pathname + window.location.search,
    source: context.source,
    message,
    ...(stack ? { stack } : {}),
    ...(context.componentStack ? { componentStack: context.componentStack } : {}),
    ...(context.boundary ? { boundary: context.boundary } : {}),
  };

  const buffer = (window.__shieldsErrors ??= []);
  buffer.push(record);
  if (buffer.length > MAX_RECORDS) buffer.splice(0, buffer.length - MAX_RECORDS);

  console.error(
    `[Shields] ${record.source} on ${record.route}: ${record.message}`,
    record.componentStack ? `\nComponent stack:${record.componentStack}` : "",
    error,
  );

  reportLovableError(error, {
    route: record.route,
    source: record.source,
    ...(record.componentStack ? { componentStack: record.componentStack } : {}),
    ...(record.boundary ? { boundary: record.boundary } : {}),
  });
}

let installed = false;

export function installClientErrorReporting() {
  if (typeof window === "undefined" || installed) return;
  installed = true;

  window.addEventListener("error", (event) => {
    const error = event.error ?? event.message;
    if (isBenignResizeObserverError(error)) {
      // Browsers emit this when ResizeObserver callbacks cause another layout pass.
      // It is not an application crash, so keep Lovable's runtime overlay from
      // replacing a healthy page with a false blank-screen error.
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    recordClientError(error, { source: "window.onerror" });
  });
  window.addEventListener("unhandledrejection", (event) => {
    if (isBenignResizeObserverError(event.reason)) {
      event.preventDefault();
      return;
    }
    recordClientError(event.reason, { source: "unhandledrejection" });
  });
}
